import { useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { ccColorDarkTeal, ccColorLightTeal, ccColorOrange, ccColorTeal } from "../components/helpersStyles";
import {Paper, Popover, Slider} from '@mui/material';
import { CcActionButton } from "./styledTexts";
import { LionColorPicker } from "../components/lionColorPicker";

/*
const points = [
  { name: "London", lng: -0.1276, lat: 51.5074 },
  { name: "Paris", lng: 2.3522, lat: 48.8566 },
];
*/

export default function CcMapPointers(props) {
  const mapContainer = useRef(null);
  const map = useRef(null);

function createMarkerElement(pinColor, circleColor) {
  const el = document.createElement('div');

  el.style.width = '27px';
  el.style.height = '41px';

  el.innerHTML = `
    <svg
      width="27"
      height="41"
      viewBox="0 0 27 41"
      xmlns="http://www.w3.org/2000/svg"
      style="display:block"
    >
      <path
        fill="${pinColor}"
        d="M13.5 0C6.04 0 0 6.04 0 13.5
           0 24 13.5 41 13.5 41
           S27 24 27 13.5
           C27 6.04 20.96 0 13.5 0Z"
      />
      <circle
        cx="13.5"
        cy="13.5"
        r="5.5"
        fill="${circleColor}"
      />
    </svg>
  `;

  return el;
}


  useEffect(() => {
    if (map.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      //style: "https://demotiles.maplibre.org/style.json",
      style: "https://tiles.openfreemap.org/styles/liberty",
      center: [0, 50],
      zoom: 4,
    });

    //map.current.addControl(new maplibregl.NavigationControl());

    map.current.addControl(
    new maplibregl.NavigationControl({
        showCompass: false,
        showZoom: true,
    })
    );
    map.current.dragRotate.disable();
    map.current.touchZoomRotate.disableRotation();


    map.current.on("load", () => {
      const bounds = new maplibregl.LngLatBounds();

      props.points.forEach((point) => {
        new maplibregl.Marker({color: ccColorTeal /*element: createMarkerElement(ccColorTeal, ccColorOrange)*/})
          .setLngLat([point.lng, point.lat])
          /*
          .setPopup(
            new maplibregl.Popup().setText(point.name)
          )
            */
          .addTo(map.current);

        bounds.extend([point.lng, point.lat]);
      });

      map.current.fitBounds(bounds, {
        padding: 60,
        maxZoom: 10,
      });
    });

    return () => map.current?.remove();
  }, []);

  return (
    <div
      ref={mapContainer}
      style={{
        width: "100%",
        height: "408px",
      }}
    />
  );
}


//------------------------------------------------------------------
//--------------------- PIN EDITOR AREA --------------------------
//------------------------------------------------------------------

const DEFAULT_PIN_STATE = {
  fillColor: "#EA4335",
  borderColor: "#B3261E",
  centerColor: "#FFFFFF",
  size: 48,
};

export function createPinSvg(state) {
  const pin = {
    ...DEFAULT_PIN_STATE,
    ...state,
  };

  return `
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="${pin.size}"
      height="${pin.size * 1.25}"
      viewBox="0 0 128 160"
    >
      <path
        d="M64 4C31.1 4 8 27.7 8 59.8C8 99.2 64 156 64 156C64 156 120 99.2 120 59.8C120 27.7 96.9 4 64 4Z"
        fill="${pin.fillColor}"
        stroke="${pin.borderColor}"
        stroke-width="6"
        stroke-linejoin="round"
      />

      <circle
        cx="64"
        cy="60"
        r="24"
        fill="${pin.centerColor}"
      />
    </svg>
  `.trim();
}

export function pinStateToBase64Png(
  state,
  { scale = 1 } = {}
) {
  return new Promise((resolve, reject) => {
    const pin = {
      ...DEFAULT_PIN_STATE,
      ...state,
    };

    const svgMarkup = createPinSvg(pin);

    const svgBlob = new Blob([svgMarkup], {
      type: "image/svg+xml;charset=utf-8",
    });

    const objectUrl = URL.createObjectURL(svgBlob);
    const image = new Image();

    image.onload = () => {
      try {
        const canvas =
          document.createElement("canvas");

        canvas.width = Math.round(
          pin.size * scale
        );

        canvas.height = Math.round(
          pin.size * 1.25 * scale
        );

        const ctx =
          canvas.getContext("2d");

        if (!ctx) {
          throw new Error(
            "Could not create canvas context."
          );
        }

        ctx.clearRect(
          0,
          0,
          canvas.width,
          canvas.height
        );

        ctx.drawImage(
          image,
          0,
          0,
          canvas.width,
          canvas.height
        );

        const base64 =
          canvas.toDataURL("image/png");

        resolve(base64);
      } catch (error) {
        reject(error);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);

      reject(
        new Error("Could not render pin SVG.")
      );
    };

    image.src = objectUrl;
  });
}

export function serializePinState(state) {
  return JSON.stringify(
    {
      fillColor: state.fillColor,
      borderColor: state.borderColor,
      centerColor: state.centerColor,
      size: state.size,
    },
    null,
    2
  );
}

export function deserializePinState(json) {
  const parsed = JSON.parse(json);

  return {
    ...DEFAULT_PIN_STATE,
    ...parsed,
  };
}



export  function MapPinEditor(props) {
  const [pinState, setPinState] = useState(DEFAULT_PIN_STATE);
  const [savedState, setSavedState] = useState("");
  const [pngBase64, setPngBase64] = useState("");

  useEffect(() => {
    //console.log(props.metadata)
    if (props.metadata) setPinState(props.metadata)
  }, [props.metadata])



  const updatePin = (key, value) => {
    setPinState((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const svgMarkup = useMemo(
    () => `
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="${pinState.size}"
        height="${pinState.size * 1.25}"
        viewBox="0 0 128 160"
      >
        <path
          d="M64 4C31.1 4 8 27.7 8 59.8C8 99.2 64 156 64 156C64 156 120 99.2 120 59.8C120 27.7 96.9 4 64 4Z"
          fill="${pinState.fillColor}"
          stroke="${pinState.borderColor}"
          stroke-width="6"
          stroke-linejoin="round"
        />

        <circle
          cx="64"
          cy="60"
          r="24"
          fill="${pinState.centerColor}"
        />
      </svg>
    `,
    [pinState]
  );

  const saveState = () => {
    const state = JSON.stringify(
      {
        version: 1,
        type: "map-pin",
        size: pinState.size,
        colors: {
          fill: pinState.fillColor,
          border: pinState.borderColor,
          center: pinState.centerColor,
        },
      },
      null,
      2
    );

    setSavedState(state);
    return state;
  };

  const loadState = () => {
    try {
      const parsed = JSON.parse(savedState);

      if (parsed.colors) {
        setPinState({
          fillColor:
            parsed.colors.fill ?? DEFAULT_PIN_STATE.fillColor,
          borderColor:
            parsed.colors.border ?? DEFAULT_PIN_STATE.borderColor,
          centerColor:
            parsed.colors.center ?? DEFAULT_PIN_STATE.centerColor,
          size:
            parsed.size ?? DEFAULT_PIN_STATE.size,
        });

        return;
      }

      // Backward compatibility with the original state shape.
      setPinState({
        fillColor:
          parsed.fillColor ?? DEFAULT_PIN_STATE.fillColor,
        borderColor:
          parsed.borderColor ?? DEFAULT_PIN_STATE.borderColor,
        centerColor:
          parsed.centerColor ?? DEFAULT_PIN_STATE.centerColor,
        size:
          parsed.size ?? DEFAULT_PIN_STATE.size,
      });
    } catch {
      alert("The saved pin state is not valid JSON.");
    }
  };
/*
  const exportAsBase64PNG = ({
    scale = 4,
  } = {}) => {
    return new Promise((resolve, reject) => {
      const svgBlob = new Blob([svgMarkup.trim()], {
        type: "image/svg+xml;charset=utf-8",
      });

      const objectUrl = URL.createObjectURL(svgBlob);
      const image = new Image();

      image.onload = () => {
        try {
          const width = pinState.size * scale;
          const height = pinState.size * 1.25 * scale;

          const canvas = document.createElement("canvas");

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");

          if (!ctx) {
            throw new Error(
              "Could not create canvas context."
            );
          }

          ctx.clearRect(
            0,
            0,
            canvas.width,
            canvas.height
          );

          ctx.drawImage(
            image,
            0,
            0,
            canvas.width,
            canvas.height
          );

          const base64 =
            canvas.toDataURL("image/png");

          setPngBase64(base64);
          resolve(base64);
        } catch (error) {
          reject(error);
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      };

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);

        reject(
          new Error("Could not render SVG.")
        );
      };

      image.src = objectUrl;
    });
  };

  const copyBase64 = async () => {
    const base64 =
      pngBase64 ||
      (await exportAsBase64PNG());

    await navigator.clipboard.writeText(base64);
  };
*/

  const exportPng = async () => {
    try {
      const base64 =
        await pinStateToBase64Png(
          pinState
        );

      setPngBase64(base64);

      return base64;
    } catch (error) {
      console.error(
        "Could not export pin:",
        error
      );
    }
  };

    const [anchorEl, setAnchorEl] = useState(null);
  
    const openPinEditor = Boolean(anchorEl);
  
    const handleOpenPinEditor = (event) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClosePinEditor = () => {
      setAnchorEl(null);
  
      //props.returnCurrentFont(currentFont.fontId, currentFont.fontName, currentFont.size, fStyle, currentFont.align, currentFontDecorations[0], currentUseAutoSize);
    };
    const handleCloseSave = () => {
      if (props.onChange) {
        props.onChange(pinState)
        handleClosePinEditor()
      }
    }

  return (<div>
          <div onClick={handleOpenPinEditor}>
            {props.customLabel}
          </div>
          <Popover      open={openPinEditor}
                    anchorEl={anchorEl}
                    onClose={handleClosePinEditor}
                    anchorOrigin={{
                      vertical: 'top',
                    }}
          >
        <Paper sx={{ padding: '10px'}} elevation={4}>
          <div className="mx-auto max-w-2xl p-6">
            <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
              <div
                className="
                  flex min-h-[140px] items-center justify-center
                  bg-[linear-gradient(45deg,#e4e4e7_25%,transparent_25%),linear-gradient(-45deg,#e4e4e7_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#e4e4e7_75%),linear-gradient(-45deg,transparent_75%,#e4e4e7_75%)]
                  bg-[length:20px_20px]
                  bg-[position:0_0,0_10px,10px_-10px,-10px_0px]
                  p-4
                "
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 128 160"
                  className="drop-shadow-lg"
                  style={{
                    width: `${pinState.size}px`,
                    height: `${
                      pinState.size * 1.25
                    }px`,
                  }}
                >
                  <path
                    d="M64 4C31.1 4 8 27.7 8 59.8C8 99.2 64 156 64 156C64 156 120 99.2 120 59.8C120 27.7 96.9 4 64 4Z"
                    fill={pinState.fillColor}
                    stroke={
                      pinState.borderColor
                    }
                    strokeWidth="6"
                    strokeLinejoin="round"
                  />

                  <circle
                    cx="64"
                    cy="60"
                    r="24"
                    fill={
                      pinState.centerColor
                    }
                  />
                </svg>
              </div>

              <div className="space-y-6 p-6">
                <div>
                  <h2 className="text-lg font-semibold text-zinc-900">
                    Marker settings
                  </h2>

                  <p className="mt-1 text-sm text-zinc-500">
                    Customize the marker colours
                    and size.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex flex-row items-center gap-x-2">
                    <div className="w-20 shrink-0 text-sm font-medium text-zinc-700">Pin</div>
                    <LionColorPicker 
                        color={pinState.fillColor}
                        onChange={(color) => updatePin('fillColor', color)}
                    />
                  </div>
                  <div className="flex flex-row items-center gap-x-2">
                    <div className="w-20 shrink-0 text-sm font-medium text-zinc-700">Border</div>
                    <LionColorPicker 
                        color={pinState.borderColor}
                        onChange={(color) => updatePin('borderColor', color)}
                    />
                  </div>
                  <div className="flex flex-row items-center gap-x-2">
                    <div className="w-20 shrink-0 text-sm font-medium text-zinc-700">Center</div>
                    <LionColorPicker 
                        color={pinState.centerColor}
                        onChange={(color) => updatePin('centerColor', color)}
                    />
                  </div>
                  
                </div>

                <div className="border-t border-zinc-100 pt-6">
                  <SizeControl
                    value={pinState.size}
                    onChange={(size) =>
                      updatePin(
                        "size",
                        size
                      )
                    }
                  />
                </div>

                <div className="flex justify-end gap-2 border-t border-zinc-100 pt-4">
                  <CcActionButton onClick={handleClosePinEditor}>Cancel</CcActionButton>
                  <CcActionButton onClick={handleCloseSave}>Done</CcActionButton>
                </div>

              </div>
            </div>
          </div>
  

        </Paper>
    </Popover>

    </div>
  );
}


export function SizeControl({
  value = 24,
  title = 'Marker size',
  unit = 'px',
  min = 24,
  max = 120,
  onChange,
}) {

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <label
          htmlFor="pin-size"
          className="text-sm font-medium text-zinc-700"
        >
          {title}
        </label>

        <div className="flex items-center gap-2">
          <input
            type="number"
            min={min}
            max={max}
            step={1}
            value={value}
            onChange={(event) => {
              const size = Number(
                event.target.value
              );

              if (!Number.isNaN(size)) {
                onChange(size);
              }
            }}
            className="
              w-20 rounded-lg border
              border-zinc-300 bg-white
              px-2 py-1.5 text-right text-sm
              text-zinc-800 shadow-sm
              outline-none
              focus:border-ccColorTeal
              focus:ring-2 focus:ring-ccColorDarkTeal/20
            "
          />

          <span className="text-sm text-zinc-500">
            {unit}
          </span>
        </div>
      </div>

      <Slider min={min} max={max}
          value={value}
          //key={value}
          onChange={(e) => onChange(Number(e.target.value))}
          
          sx={{
            '& .MuiSlider-thumb': {
              backgroundColor: ccColorTeal,
              border: `2px solid ${ccColorDarkTeal}`,
            },

            '& .MuiSlider-track': {
              backgroundColor: ccColorLightTeal,
              border: 'none',
            },

            '& .MuiSlider-rail': {
              backgroundColor: '#d1d5db',
              opacity: 1,
            },            
          }}
      />

      <div className="flex justify-between text-xs text-zinc-400">
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
}
